import { useState, useEffect } from 'react'
import { Plus, Trash2, Loader2, Presentation, Clock, Link2 } from 'lucide-react'
import { demoPrepApi, dealsApi } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { showToast } from '@/store/uiStore'
import type { DemoPrepPlan } from './types'
import { STATUS_CONFIG, PROSPECT_INDUSTRIES, isRunsheet } from './types'
import { DemoPlanDetail } from './components/DemoPlanDetail'

function unwrap(res: any) {
  return res?.data?.data || res?.data || null
}

function unwrapList(res: any): any[] {
  const d = res?.data?.data || res?.data || []
  return Array.isArray(d) ? d : []
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

interface DealOption {
  id: number
  customer_name: string
  hubspot_deal_name?: string
}

export function DemoPrepTool() {
  const { user } = useAuthStore()
  const [plans, setPlans] = useState<DemoPrepPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [deals, setDeals] = useState<DealOption[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newDealId, setNewDealId] = useState<string>('')
  const [newIndustry, setNewIndustry] = useState<string>('')

  const loadList = async () => {
    setLoading(true)
    const res = await demoPrepApi.list()
    setPlans(unwrapList(res) as DemoPrepPlan[])
    setLoading(false)
  }

  const loadDeals = async () => {
    const res = await dealsApi.list({ pageSize: 200 })
    if (res.success && res.data) {
      const dealData = (res.data as any).deals || (res.data as any).data?.deals || []
      setDeals(Array.isArray(dealData) ? dealData : [])
    }
  }

  useEffect(() => {
    loadList()
    loadDeals()
  }, [])

  const handleCreate = async () => {
    setCreating(true)
    const res = await demoPrepApi.create({
      title: newTitle || 'Untitled Demo Plan',
      deal_id: newDealId ? parseInt(newDealId) : undefined,
      prospect_industry: newIndustry || undefined,
      created_by: user?.email || undefined,
      created_by_name: user?.name || undefined,
    })
    const data = unwrap(res)
    if (data) {
      setSelectedId((data as DemoPrepPlan).id)
      setShowCreateModal(false)
      setNewTitle('')
      setNewDealId('')
      setNewIndustry('')
    }
    setCreating(false)
  }

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    await demoPrepApi.remove(id)
    setPlans(plans.filter(p => p.id !== id))
    showToast('Demo plan deleted', 'success')
  }

  if (selectedId) {
    return <DemoPlanDetail id={selectedId} onBack={() => { setSelectedId(null); loadList() }} />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Demo Prep</h2>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            AI-generated talking points for FrankieOne product demos
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-orange-600 text-white hover:bg-orange-500 transition-colors"
        >
          <Plus size={14} />
          New Demo Plan
        </button>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Create Demo Plan</h3>
          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Title</label>
            <input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-color)]"
              placeholder="e.g. Acme Corp Initial Demo"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Prospect Industry</label>
            <select
              value={newIndustry}
              onChange={e => setNewIndustry(e.target.value)}
              className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
            >
              <option value="">Select industry...</option>
              {PROSPECT_INDUSTRIES.map(ind => (
                <option key={ind.value} value={ind.value}>{ind.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Link to Deal (optional)</label>
            <select
              value={newDealId}
              onChange={e => setNewDealId(e.target.value)}
              className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
            >
              <option value="">No deal linked</option>
              {deals.map(d => (
                <option key={d.id} value={d.id}>
                  {d.customer_name}{d.hubspot_deal_name ? ` — ${d.hubspot_deal_name}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => { setShowCreateModal(false); setNewTitle(''); setNewDealId(''); setNewIndustry('') }}
              className="px-3 py-2 text-sm rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-orange-600 text-white hover:bg-orange-500 transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create
            </button>
          </div>
        </div>
      )}

      {/* Plans List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-[var(--text-muted)]" /></div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16">
          <Presentation size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <p className="text-sm text-[var(--text-muted)]">No demo plans yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {plans.map(plan => {
            const status = STATUS_CONFIG[plan.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.draft
            return (
              <div
                key={plan.id}
                onClick={() => setSelectedId(plan.id)}
                className="flex items-center justify-between p-4 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors cursor-pointer border border-[var(--border-color)]"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-medium text-[var(--text-primary)] truncate">{plan.title}</span>
                    <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
                    {plan.prospect_industry && (
                      <span className="px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 text-[10px] font-medium">
                        {PROSPECT_INDUSTRIES.find(i => i.value === plan.prospect_industry)?.label || plan.prospect_industry}
                      </span>
                    )}
                    {plan.customer_name && (
                      <span className="flex items-center gap-1"><Link2 size={11} />{plan.customer_name}</span>
                    )}
                    {isRunsheet(plan.talking_points) && (
                      <span>{plan.talking_points.sections.length} sections</span>
                    )}
                    {plan.created_by_name && <span>by {plan.created_by_name}</span>}
                    <span className="flex items-center gap-1"><Clock size={12} />{formatDate(plan.created_at)}</span>
                  </div>
                </div>
                <button
                  onClick={e => handleDelete(plan.id, e)}
                  className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
